/// <amd-module name="Wit/QueriesHub/QueriesHubComponent" />

import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Observer } from "VSSUI/Observer";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { IQueryService, QueryHubView } from "Wit/Common/QueryService/IQueryService";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import { ChartsComponent } from "Wit/QueriesHub/Charts/QueryCharts";
import { ContributedTabView } from "Wit/QueriesHub/ContributedTabView/ContributedTabView";
import { QueryEditor } from "Wit/QueriesHub/Editor/QueryEditor";
import * as Resources from "Wit/QueriesHub/Resources";
import { ResultsViewComponent } from "Wit/QueriesHub/ResultsView/ResultsViewComponent";
import { WorkItemEditComponent } from "Wit/QueriesHub/WorkItemEdit/WorkItemEditComponent";
import { ZeroData } from "VSSUI/ZeroData";
import { getAssetLocation } from "VSS/Platform/Location";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";

class QueriesHubComponent extends VssComponent<{}, {}> {
    private _queryService: IQueryService;
    private _selectedView: QueryHubView | string | undefined;

    private _globalMessagesService: IGlobalMessagesService;
    private isShowMoreItemsSet: ObservableValue<boolean> = new ObservableValue(false);

    public constructor(props: {}, context?: IVssComponentContext) {
        super(props, context);

        this._queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        this._globalMessagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
    }

    public componentDidMount() {
        this._queryService.query.subscribe(this.setDocumentTitle);
        this._queryService.view.subscribe(this.setDocumentTitle);

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.NEW_BOARDS_PLATFORM, {
            source: "Queries"
        });
    }

    public componentWillUnmount() {
        this._queryService.query.unsubscribe(this.setDocumentTitle);
        this._queryService.view.unsubscribe(this.setDocumentTitle);
    }

    public render(): Exclude<React.ReactNode, null | undefined> {
        return (
            <PerformanceScenario area={QueriesHubConstants.Area} scenarioName={QueriesHubConstants.QueriesHubViewed} tti={true}>
                <Observer
                    view={this._queryService.view}
                    error={{
                        observableValue: this._queryService.queryLoadingError,
                        filter: (error: string) => {
                            if (error && !this._queryService.query.value) {
                                this._globalMessagesService.closeBanner();
                            }
                            return true;
                        }
                    }}
                >
                    {({ view, error }: { view: QueryHubView | string; error: string | undefined }): Exclude<React.ReactNode, null | undefined> => {
                        return (
                            <PerformanceScenarioContext.Consumer>
                                {scenarioProps => {
                                    if (this._selectedView && this._selectedView !== view) {
                                        scenarioProps.resetScenario();
                                    }

                                    if (error && !this._queryService.query.value) {
                                        return (
                                            <div className="full-height full-width flex-column justify-center">
                                                <ZeroData
                                                    primaryText={Resources.QueriesError}
                                                    secondaryText={error}
                                                    imagePath={getAssetLocation(
                                                        this.context.pageContext,
                                                        "ms.vss-work-web/new-queries-hub-content/Illustrations/general-no-results-found.svg"
                                                    )}
                                                    imageAltText={Resources.QueriesError}
                                                />
                                            </div>
                                        );
                                    }

                                    this._selectedView = view;
                                    switch (view) {
                                        case QueryHubView.QueryCharts:
                                            return <ChartsComponent telemetryProps={{
                                                parentPageName: "QueriesHub-ChartsTab",
                                            }}/>;
                                        case QueryHubView.QueryResults:
                                            return <ResultsViewComponent isShowMoreItemsSet={this.isShowMoreItemsSet} />;
                                        case QueryHubView.QueryEdit:
                                            return <QueryEditor isShowMoreItemsSet={this.isShowMoreItemsSet} />;
                                        case QueryHubView.WorkItemEdit:
                                            return <WorkItemEditComponent />;
                                        default:
                                            // assume it's a contributed tab
                                            return <ContributedTabView key={view} />;
                                    }
                                }}
                            </PerformanceScenarioContext.Consumer>
                        );
                    }}
                </Observer>
            </PerformanceScenario>
        );
    }

    private setDocumentTitle = (): void => {
        const view = this._queryService.view.value;
        if (view === QueryHubView.WorkItemEdit) {
            return;
        }

        const queryName = this._queryService.query.value?.name ?? Resources.UntitledQuery;
        document.title = format(Resources.QueriesDocumentTitle, queryName);
    };
}

Components.add("queries-hub-component", QueriesHubComponent);
